import {
  Button,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import React from 'react';
import './MerlinFeedback.css';
import {
  RATING_ADD_DETAILS_LABEL,
  RATING_DOWN_LABEL,
  RATING_PROMPT,
  RATING_UP_LABEL,
  ratingRecordedNotice,
} from './constants';
import { ControlsState, MemoryRefState, Rating, StreamState } from './types';

/**
 * The per-response rating controls in chat.
 *
 * The control state is one pure derivation, so the total-function property is structural rather than
 * something the component has to remember to honour.
 */

/**
 * `controlsStateFor(stream, memoryRefState, ratingInFlight)`.
 *
 * Written as guards rather than a `switch` so it is **total by construction**: every path returns, and
 * both enum widenings fail safe. A stream state added later falls to `hidden`; a `MemoryRefState` added
 * later falls to `visible-disabled`. Neither can accidentally become operable, which is the direction
 * that matters — an operable control without a resolved identifier is a rating that cannot be stored.
 */
export function controlsStateFor(
  stream: StreamState,
  memoryRefState: MemoryRefState,
  ratingInFlight: boolean
): ControlsState {
  // Nothing to rate before completion. This is a different situation from a complete response whose
  // identifier has not arrived — that one shows inoperable controls rather than nothing at all.
  if (stream !== 'complete') return 'hidden';

  /**
   * No event, no controls.
   *
   * `unavailable` means `CreateEvent` did not land after retries, so no memory event exists for this
   * response and nothing a rating could point at will ever appear. Offering a control there is offering
   * to record something we cannot store — the reader spends a click to be told no. Hiding is not a
   * degraded state; it is the accurate one.
   *
   * This supersedes the earlier "visible but inoperable" treatment, which existed so the refusal could
   * be *explained* on activation rather than being silent. That reasoning assumed the click could at
   * least carry a retry, and it cannot: the retry needs the turn text, which is no longer held by the
   * time the state settles. So the control was buying an explanation for a dead end.
   */
  if (memoryRefState === 'unavailable') return 'hidden';

  // `resolving` is visible-disabled rather than hidden: the controls take their final position the
  // moment the response completes and only change *state* afterwards, so resolution shifts no layout.
  // Hiding here would move the page under content the reader is already on.
  if (memoryRefState !== 'resolved') return 'visible-disabled';

  if (ratingInFlight) return 'visible-disabled';

  return 'visible-enabled';
}

export interface MessageRatingControlsProps {
  stream: StreamState;
  memoryRefState: MemoryRefState;
  ratingInFlight: boolean;
  /** The Rating already recorded in this page session, if any. */
  recordedRating?: Rating | null;
  /** Thumbs-down opens the form. Thumbs-up records directly and opens nothing. */
  onThumbsDown: () => void;
  onThumbsUp: () => void;
  /**
   * Opens the form on an **already recorded** rating, so a comment can be added without re-rating.
   *
   * Optional so the component still renders where no such affordance is wired; the link is simply absent
   * rather than present and inert.
   */
  onAddDetails?: () => void;
}

export const MessageRatingControls: React.FC<MessageRatingControlsProps> = ({
  stream,
  memoryRefState,
  ratingInFlight,
  recordedRating = null,
  onThumbsDown,
  onThumbsUp,
  onAddDetails,
}) => {
  const state = controlsStateFor(stream, memoryRefState, ratingInFlight);

  if (state === 'hidden') return null;

  /**
   * The only visible non-operable state left is `resolving`, where the event may still land.
   *
   * There is nothing to explain in that window, and nothing to retry — so the controls are genuinely
   * disabled rather than clickable-but-refusing. The refusal notice and the retry affordance both went
   * with `unavailable`, which no longer renders at all.
   */
  const operable = state === 'visible-enabled';

  return (
    <div className="mfb-rating-controls" data-testid="merlin-rating-controls">
      <SpaceBetween size="xs" direction="horizontal">
        {/*
          Prompt before, acknowledgement after — one slot, never both. The prompt is a question, and once
          it has been answered leaving it up alongside a confirmation reads as if it were asking again.
        */}
        {recordedRating === null ? (
          <span
            className="mfb-rating-notice mfb-rating-prompt"
            data-testid="merlin-rating-prompt"
          >
            {RATING_PROMPT}
          </span>
        ) : (
          <span
            className="mfb-rating-notice"
            data-testid="merlin-rating-recorded"
          >
            <StatusIndicator type="success">
              {ratingRecordedNotice(recordedRating)}
            </StatusIndicator>
          </span>
        )}
        {/*
          `link`, so the controls sit in the transcript without the chrome of a boxed button. Two bordered
          buttons under every answer compete with the answer itself, and the `primary` fill on the active
          one read as a call to action rather than as a record of one.

          The selected state that fill provided is not dropped, it moved: `ratingRecordedNotice` names the
          rating, which a borderless button has no way to show.
        */}
        <Button
          disabled={!operable}
          variant="link"
          // Icon plus text: the icon makes the polarity readable at a glance, the text keeps it
          // unambiguous when the icon fails to render or is read by a screen reader.
          iconName="thumbs-up"
          ariaLabel="Rate this response helpful"
          onClick={() => operable && onThumbsUp()}
          data-testid="merlin-thumbs-up"
        >
          {RATING_UP_LABEL}
        </Button>
        <Button
          disabled={!operable}
          variant="link"
          iconName="thumbs-down"
          ariaLabel="Rate this response not helpful"
          onClick={() => operable && onThumbsDown()}
          data-testid="merlin-thumbs-down"
        >
          {RATING_DOWN_LABEL}
        </Button>
        {/*
          Only once something is recorded, and only if a handler exists. Before a rating there is nothing to
          add details to, and the two rating controls are the whole decision — a third control alongside
          them would compete with the choice rather than follow it.
        */}
        {recordedRating !== null && onAddDetails ? (
          <Button
            disabled={!operable}
            variant="link"
            iconName="edit"
            ariaLabel="Add details to your feedback"
            onClick={() => operable && onAddDetails()}
            data-testid="merlin-add-details"
          >
            {RATING_ADD_DETAILS_LABEL}
          </Button>
        ) : null}
      </SpaceBetween>
    </div>
  );
};
